/**
 * header.js — Site header Web Component.
 *
 * Responsibilities:
 *   1. Tab switching — driven by nav menu level-1 items, persisted to sessionStorage
 *   2. Dropdown open/close — CSS handles hover; JS handles keyboard + Escape
 *   3. Cart count live updates via 'cart:item-added' custom event
 *
 * HTML contract:
 *   <site-header>
 *     <a data-tab="dame" aria-expanded="true">Dame</a>
 *     <nav class="site-header__nav is-active" data-tab-nav="dame">
 *       <div class="site-header__nav-item site-header__nav-item--has-dropdown" data-nav-item="klaer">
 *         <a class="site-header__nav-link" aria-haspopup="true" aria-expanded="false">Klær</a>
 *         <div class="site-header__dropdown" aria-hidden="true">...</div>
 *       </div>
 *     </nav>
 *     <a class="site-header__cart"><span data-cart-count>3</span></a>
 *   </site-header>
 */
class SiteHeader extends TRYComponent {
  #activeTab = '';
  #openNavItem = null;
  #handleKeydown = null;
  #handleClickOutside = null;
  #handleCartUpdate = null;
  #wishlistObserver = null;
  #wishlistDomObserver = null;
  #ro = null;
  #lastScrollY = 0;
  #rafPending = false;
  #handleScroll = null;

  connectedCallback() {
    super.connectedCallback();

    // Keep --site-header-height in sync so the mobile-menu starts below the header
    const setHeight = () => {
      const section = this.closest('.section-header');
      if (section?.classList.contains('is-hidden')) return;
      document.documentElement.style.setProperty('--site-header-height', `${this.offsetHeight}px`);
    };
    setHeight();
    this.#ro = new ResizeObserver(setHeight);
    this.#ro.observe(this);

    const THRESHOLD = 80;
    this.#lastScrollY = window.scrollY;
    this.#handleScroll = () => {
      if (this.#rafPending) return;
      this.#rafPending = true;
      requestAnimationFrame(() => {
        this.#rafPending = false;
        const y = window.scrollY;
        if (window.innerWidth < 768) { this.#lastScrollY = y; return; }
        const section = this.closest('.section-header');
        if (!section) { this.#lastScrollY = y; return; }

        if (y < THRESHOLD || y < this.#lastScrollY) {
          if (section.classList.contains('is-hidden')) {
            section.classList.remove('is-hidden');
            document.documentElement.style.setProperty(
              '--site-header-height', `${this.offsetHeight}px`
            );
          }
        } else if (y > this.#lastScrollY && !this.#openNavItem) {
          if (!section.classList.contains('is-hidden')) {
            section.classList.add('is-hidden');
            const barH = (this.querySelector('.announcement-bar')?.offsetHeight ?? 0)
              + (this.querySelector('.site-header__bar')?.offsetHeight ?? 0);
            document.documentElement.style.setProperty(
              '--site-header-height', `${barH}px`
            );
          }
        }
        this.#lastScrollY = y;
      });
    };
    window.addEventListener('scroll', this.#handleScroll, { passive: true });

    // Restore tab: current-page tab (from Liquid) > sessionStorage > first tab.
    // A page-derived tab is persisted so the context survives to pages that
    // can't derive one themselves (search, cart, unisex products) — without it,
    // browsing Herre collections and clicking into a product fell back to Dame.
    const firstTab = this.querySelector('[data-tab]')?.dataset.tab ?? '';
    const pageTab = this.dataset.activeTab ?? '';
    try {
      const stored = sessionStorage.getItem('bogart-active-tab');
      const validStored = stored && this.querySelector(`[data-tab="${stored}"]`);
      this.#switchTab(pageTab || (validStored ? stored : firstTab), Boolean(pageTab));
    } catch {
      // Storage unavailable (private browsing, iframe restrictions, etc.)
      this.#switchTab(pageTab || firstTab, false);
    }

    // Delegated click handler — catches tab buttons, nav links, mobile trigger, and search
    this.addEventListener('click', e => {
      // Tab switching buttons
      const tabBtn = e.target.closest('[data-tab]');
      if (tabBtn) {
        this.#switchTab(tabBtn.dataset.tab);
        return;
      }

      // Nav links with dropdowns — prevent navigation, toggle dropdown
      const navLink = e.target.closest('.site-header__nav-link[aria-haspopup]');
      if (navLink) {
        e.preventDefault();
        const item = navLink.closest('.site-header__nav-item');
        if (item.classList.contains('is-open')) {
          this.#closeDropdown(item);
        } else {
          this.#openDropdown(item);
        }
        return;
      }

      // Mobile menu trigger
      const mobileBtn = e.target.closest('[data-mobile-open]');
      if (mobileBtn) {
        const menu = this.closest('.section-header')?.querySelector('mobile-menu');
        if (menu?.classList.contains('is-open')) {
          menu.close();
        } else {
          menu?.open();
        }
        return;
      }

      // Search toggle
      if (e.target.closest('[data-search-toggle]')) {
        this.#toggleSearch();
        return;
      }
    });

    // Space key on nav links with dropdown (Space does not fire click on <a>)
    this.addEventListener('keydown', e => {
      if (e.key !== ' ') return;
      const navLink = e.target.closest('.site-header__nav-link[aria-haspopup]');
      if (!navLink) return;
      e.preventDefault();
      const item = navLink.closest('.site-header__nav-item');
      if (item.classList.contains('is-open')) {
        this.#closeDropdown(item);
      } else {
        this.#openDropdown(item);
      }
    });

    // Escape closes any open dropdown
    this.#handleKeydown = e => {
      if (e.key !== 'Escape' || !this.#openNavItem) return;
      const item = this.#openNavItem;
      this.#closeDropdown(item);
      item.querySelector('.site-header__nav-link')?.focus();
    };
    document.addEventListener('keydown', this.#handleKeydown);

    // Click outside the header closes all dropdowns
    this.#handleClickOutside = e => {
      if (!this.contains(e.target)) this.#closeAll();
    };
    document.addEventListener('click', this.#handleClickOutside);

    // Cart count: update all badges (main header + mobile menu)
    this.#handleCartUpdate = e => {
      const count = e.detail?.cart?.item_count;
      if (count == null) return;
      for (const badge of this.querySelectorAll('[data-cart-count]')) {
        badge.textContent = count;
        if (count > 0) {
          badge.removeAttribute('hidden');
        } else {
          badge.setAttribute('hidden', '');
        }
      }
    };
    document.addEventListener('cart:item-added', this.#handleCartUpdate);

    // Wishlist count: mirror WK's counter into the header badge.
    this.#initWishlistCount();
  }

  // Wishlist count: mirror Wishlist King's .wk-counter into our header badge(s).
  // WK owns the count and keeps .wk-counter updated; we reflect it, matching the
  // cart-count pattern in #handleCartUpdate.
  #initWishlistCount() {
    const counter = document.querySelector('.wk-counter');
    if (counter) {
      this.#attachWishlistCounter(counter);
      return;
    }
    // WK hydrates asynchronously — watch the DOM until its counter appears.
    this.#wishlistDomObserver = new MutationObserver(() => {
      const el = document.querySelector('.wk-counter');
      if (!el) return;
      this.#wishlistDomObserver.disconnect();
      this.#wishlistDomObserver = null;
      this.#attachWishlistCounter(el);
    });
    this.#wishlistDomObserver.observe(document.body, { childList: true, subtree: true });
  }

  #attachWishlistCounter(counter) {
    this.#updateWishlistCount(counter);
    this.#wishlistObserver = new MutationObserver(() => this.#updateWishlistCount(counter));
    this.#wishlistObserver.observe(counter, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  #updateWishlistCount(counter) {
    const count = parseInt(counter.textContent.trim(), 10) || 0;
    for (const badge of this.querySelectorAll('[data-wishlist-count]')) {
      badge.textContent = count;
      if (count > 0) {
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    }
  }

  disconnectedCallback() {
    this.#ro?.disconnect();
    window.removeEventListener('scroll', this.#handleScroll);
    document.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('click', this.#handleClickOutside);
    document.removeEventListener('cart:item-added', this.#handleCartUpdate);
    this.#wishlistObserver?.disconnect();
    this.#wishlistDomObserver?.disconnect();
  }

  /**
   * Switch the active navigation tab.
   * @param {string} tab - Handle of the tab to activate
   * @param {boolean} persist - Whether to write to sessionStorage (default: true)
   */
  #switchTab(tab, persist = true) {
    this.#closeAll();
    this.#activeTab = tab;

    for (const btn of this.querySelectorAll('[data-tab]')) {
      btn.setAttribute('aria-expanded', btn.dataset.tab === tab ? 'true' : 'false');
    }

    for (const nav of this.querySelectorAll('[data-tab-nav]')) {
      nav.classList.toggle('is-active', nav.dataset.tabNav === tab);
    }

    if (persist) {
      try {
        sessionStorage.setItem('bogart-active-tab', tab);
      } catch {
        // Storage unavailable
      }
    }
  }

  /**
   * Open the dropdown for a nav item (keyboard path).
   * @param {Element} navItem
   */
  #openDropdown(navItem) {
    this.#closeAll();
    navItem.classList.add('is-open');
    navItem.querySelector('.site-header__dropdown')?.setAttribute('aria-hidden', 'false');
    navItem.querySelector('.site-header__nav-link')?.setAttribute('aria-expanded', 'true');
    this.#openNavItem = navItem;
  }

  /**
   * Close the dropdown for a nav item.
   * @param {Element} navItem
   */
  #closeDropdown(navItem) {
    navItem.classList.remove('is-open');
    navItem.querySelector('.site-header__dropdown')?.setAttribute('aria-hidden', 'true');
    navItem.querySelector('.site-header__nav-link')?.setAttribute('aria-expanded', 'false');
    if (this.#openNavItem === navItem) this.#openNavItem = null;
  }

  /** Close all open dropdowns. */
  #closeAll() {
    for (const item of this.querySelectorAll('.site-header__nav-item.is-open')) {
      this.#closeDropdown(item);
    }
    this.#openNavItem = null;
  }

  #toggleSearch() {
    const drawer = document.querySelector('search-drawer');
    if (drawer?.classList.contains('is-open')) {
      drawer.close();
    } else {
      drawer?.open();
    }
  }
}

customElements.define('site-header', SiteHeader);

/**
 * mobile-menu — Drawer web component.
 *
 * Responsibilities:
 *   1. Open / close the drawer (with body scroll lock)
 *   2. Tab switching between Dame / Herre panels
 *   3. Sub-panel drill-down (category → sub-links) with back navigation
 *   4. Escape key handling
 *
 * HTML contract:
 *   <mobile-menu>
 *     <div class="mobile-menu__drawer">
 *       <button data-mobile-close>…</button>
 *       <button data-mobile-tab="dame" aria-pressed="true">…</button>
 *       <div data-mobile-panel="dame" class="is-active">
 *         <button data-open-subpanel="dame-klaer">…</button>
 *         <div data-mobile-subpanel="dame-klaer">
 *           <button data-mobile-back>…</button>
 *         </div>
 *       </div>
 *     </div>
 *   </mobile-menu>
 */
class MobileMenu extends HTMLElement {
  #activeTab = '';
  #currentSubpanel = null;
  #handleKeydown = null;
  #trap = null;

  connectedCallback() {
    // Close button
    this.querySelector('[data-mobile-close]')?.addEventListener('click', () => this.close());

    // Click on overlay backdrop (outside drawer)
    this.addEventListener('click', e => {
      if (e.target === this) this.close();
    });

    // Tab switching — swaps the visible panel; drawer stays open
    for (const btn of this.querySelectorAll('[data-mobile-tab]')) {
      btn.addEventListener('click', e => {
        e.preventDefault();
        this.#switchTab(btn.dataset.mobileTab);
      });
    }

    // Drill-down: open a sub-panel
    for (const btn of this.querySelectorAll('[data-open-subpanel]')) {
      btn.addEventListener('click', () => this.#showSubpanel(btn.dataset.openSubpanel));
    }

    // Back: close current sub-panel
    for (const btn of this.querySelectorAll('[data-mobile-back]')) {
      btn.addEventListener('click', () => this.#hideSubpanel());
    }

    // Escape: close sub-panel or menu
    this.#handleKeydown = e => {
      if (e.key !== 'Escape') return;
      if (this.#currentSubpanel) {
        this.#hideSubpanel();
      } else {
        this.close();
      }
    };
    document.addEventListener('keydown', this.#handleKeydown);

    // Initialise active tab — prefer page-active tab, fall back to first
    const firstTab = this.querySelector('[data-mobile-tab]')?.dataset.mobileTab ?? '';
    const pageTab = this.closest('site-header')?.dataset.activeTab ?? '';
    const initialTab = (pageTab && this.querySelector(`[data-mobile-tab="${pageTab}"]`)) ? pageTab : firstTab;
    if (initialTab) this.#switchTab(initialTab);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#handleKeydown);
    this.#trap?.release({ restoreFocus: false });
  }

  open() {
    if (this.classList.contains('is-open')) return;
    // Pin the drawer to the header's live bottom edge so the tabs butt right up
    // under it — avoids gaps from a stale cached --site-header-height (font swap,
    // scroll-hide state, or a desktop→mobile resize).
    const header = this.closest('.section-header');
    if (header) {
      const bottom = header.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty('--site-header-height', `${Math.max(0, bottom)}px`);
    }
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.closest('.section-header')?.querySelector('[data-mobile-open]')?.setAttribute('aria-expanded', 'true');
    this.#trap = new FocusTrap(this);
    // Suppress the focus ring for this automatic open-focus only — a real
    // Tab press should still show it, so drop the class on the next keydown.
    this.classList.add('mobile-menu--skip-focus-ring');
    document.addEventListener('keydown', () => {
      this.classList.remove('mobile-menu--skip-focus-ring');
    }, { once: true });
    this.#trap.activate(this.querySelector('[data-mobile-tab]'));
  }

  close() {
    this.classList.remove('is-open');
    this.#trap?.release();
    this.#trap = null;
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.closest('.section-header')?.querySelector('[data-mobile-open]')?.setAttribute('aria-expanded', 'false');
  }

  #switchTab(tab) {
    this.#activeTab = tab;
    for (const btn of this.querySelectorAll('[data-mobile-tab]')) {
      btn.setAttribute('aria-expanded', btn.dataset.mobileTab === tab ? 'true' : 'false');
    }
    for (const panel of this.querySelectorAll('[data-mobile-panel]')) {
      panel.classList.toggle('is-active', panel.dataset.mobilePanel === tab);
    }
    this.#hideSubpanel();
  }

  #showSubpanel(id) {
    this.#currentSubpanel?.classList.remove('is-open');
    const panel = this.querySelector(`[data-mobile-subpanel="${id}"]`);
    if (!panel) return;
    panel.classList.add('is-open');
    this.#currentSubpanel = panel;
  }

  #hideSubpanel() {
    this.#currentSubpanel?.classList.remove('is-open');
    this.#currentSubpanel = null;
  }
}

customElements.define('mobile-menu', MobileMenu);

/**
 * search-drawer — Predictive search side drawer.
 *
 * States:
 *   empty   — shown on open; popular categories (from Liquid) + popular products (API)
 *   results — shown while typing; suggestions + product hits
 *   no-results — shown when API returns nothing
 *
 * HTML contract:
 *   <search-drawer>
 *     <div class="search-drawer__panel">
 *       <input class="search-drawer__input">
 *       <button data-search-clear hidden>Tøm</button>
 *       <button class="search-drawer__submit">…search icon…</button>
 *       <div data-search-empty>
 *         <div data-search-popular-products><ul></ul></div>
 *       </div>
 *       <div data-search-results hidden>
 *         <div data-search-no-results hidden>…</div>
 *         <div data-search-suggestions-section hidden><ul data-search-suggestions></ul></div>
 *         <hr data-search-divider hidden>
 *         <div data-search-products-section hidden><ul data-search-products></ul></div>
 *       </div>
 *       <button data-search-close>…</button>
 *     </div>
 *   </search-drawer>
 */
class SearchDrawer extends HTMLElement {
  #input = null;
  #trap = null;
  #clearBtn = null;
  #emptyEl = null;
  #resultsEl = null;
  #noResultsEl = null;
  #suggestionsSection = null;
  #suggestionsList = null;
  #divider = null;
  #productsSection = null;
  #productsList = null;
  #handleKeydown = null;
  #debounceTimer = null;
  #abortController = null;

  connectedCallback() {
    this.#input             = this.querySelector('.search-drawer__input');
    this.#clearBtn          = this.querySelector('[data-search-clear]');
    this.#emptyEl           = this.querySelector('[data-search-empty]');
    this.#resultsEl         = this.querySelector('[data-search-results]');
    this.#noResultsEl       = this.querySelector('[data-search-no-results]');
    this.#suggestionsSection = this.querySelector('[data-search-suggestions-section]');
    this.#suggestionsList   = this.querySelector('[data-search-suggestions]');
    this.#divider           = this.querySelector('[data-search-divider]');
    this.#productsSection   = this.querySelector('[data-search-products-section]');
    this.#productsList      = this.querySelector('[data-search-products]');

    // Backdrop click closes drawer
    this.addEventListener('click', e => { if (e.target === this) this.close(); });

    // Close button
    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.close());

    // Submit button → navigate to search results page
    this.querySelector('.search-drawer__submit')?.addEventListener('click', () => {
      const q = this.#input?.value.trim();
      if (q) window.location.href = `${window.Shopify?.routes?.root ?? '/'}search?q=${encodeURIComponent(q)}&type=product`;
    });

    // Input → debounced predictive search (skipped when Voyado handles it)
    this.#input?.addEventListener('input', () => {
      const val = this.#input.value.trim();
      this.#clearBtn?.toggleAttribute('hidden', val.length === 0);
      if (window.elevate) return;
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = setTimeout(() => this.#search(val), 280);
    });

    // Enter key → navigate
    this.#input?.addEventListener('keydown', e => {
      if (e.key !== 'Enter') return;
      const q = this.#input.value.trim();
      if (q) window.location.href = `${window.Shopify?.routes?.root ?? '/'}search?q=${encodeURIComponent(q)}&type=product`;
    });

    // Clear button
    this.#clearBtn?.addEventListener('click', () => {
      this.#input.value = '';
      this.#clearBtn.setAttribute('hidden', '');
      this.#showEmpty();
      this.#input.focus();
    });

    // Escape
    this.#handleKeydown = e => { if (e.key === 'Escape') this.close(); };
    document.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#handleKeydown);
    this.#trap?.release({ restoreFocus: false });
  }

  open() {
    if (this.classList.contains('is-open')) return;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.#trap = new FocusTrap(this);
    this.#trap.activate(this.#input);
  }

  close() {
    this.classList.remove('is-open');
    this.#trap?.release();
    this.#trap = null;
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  #showEmpty() {
    this.#emptyEl?.removeAttribute('hidden');
    this.#resultsEl?.setAttribute('hidden', '');
  }

  #showResults() {
    this.#emptyEl?.setAttribute('hidden', '');
    this.#resultsEl?.removeAttribute('hidden');
  }

  async #search(query) {
    if (!query) { this.#showEmpty(); return; }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,query&resources[limit]=3`;
      const res = await fetch(url, { signal: this.#abortController.signal });
      if (!res.ok) return;
      const data = await res.json();

      const products = data.resources?.results?.products ?? [];
      const queries  = data.resources?.results?.queries  ?? [];
      const hasProducts = products.length > 0;
      const hasQueries  = queries.length > 0;

      this.#showResults();
      this.#noResultsEl?.toggleAttribute('hidden', hasProducts || hasQueries);
      this.#suggestionsSection?.toggleAttribute('hidden', !hasQueries);
      this.#divider?.toggleAttribute('hidden', !(hasQueries && hasProducts));
      this.#productsSection?.toggleAttribute('hidden', !hasProducts);

      if (this.#suggestionsList) {
        this.#suggestionsList.innerHTML = queries.map(q => `
          <li>
            <a class="search-drawer__suggestion-link" href="${q.url}">
              ${this.#highlight(this.#escapeHtml(q.text), query)}
            </a>
          </li>`).join('');
      }

      if (this.#productsList) {
        this.#productsList.innerHTML = products.map(p => this.#productHTML(p)).join('');
      }
    } catch (e) {
      if (e.name !== 'AbortError') console.error(e);
    }
  }

  #productHTML(product) {
    const img = product.image
      ? `<img class="search-drawer__product-img" src="${product.image}" alt="${this.#escapeHtml(product.title)}" loading="lazy">`
      : '';
    const price = product.price ? `<p class="search-drawer__product-price">${product.price}</p>` : '';
    return `
      <li>
        <a class="search-drawer__product-link" href="${product.url}">
          <div class="search-drawer__product-img-wrap">${img}</div>
          <div>
            <p class="search-drawer__product-name">${this.#escapeHtml(product.title)}</p>
            ${price}
          </div>
        </a>
      </li>`;
  }

  #highlight(safeText, query) {
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return safeText.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>');
  }

  #escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('search-drawer', SearchDrawer);
